import { useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { GeoJSON, Marker, Pane, Polyline, Popup, Tooltip } from "react-leaflet";
import bezierSpline from "@turf/bezier-spline";
import { lineString } from "@turf/helpers";
import { divIcon, DomEvent } from "leaflet";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { Button, Card, Dialog, DialogTitle, Stack, Typography } from "@mui/material";

import { MAP_CONTENTS_OPACITY, ROUTE_SIZE, STATIONS_COLOR, STATIONS_SIZE, SMALL_STATIONS_SIZE } from "Configs/map";
import { IPath } from "Types/path";
import { IStation } from "Types/station";

import useEditPath from "Hooks/useEditPath";
import usePaths from "Hooks/usePaths";
import { useAppDispatch, useAppSelector } from "Hooks/useRedux";

import { changeModal } from "Redux/modalSlice";
import { setTooltip } from "Redux/tooltipSlice";

import HintBox from "Components/HintBox";
import { STATIONS2 } from "Constants/Stations";
import { Box } from "@mui/system";
import { changeContextPosition2, changeShowContext2, changeStoredStation } from "Redux/mapSlice";
import { changeShowPaths, changeShowReportRightControl } from "Redux/sideSlice";

const ICON_SIZE = 22;

const Colors = [
  "#EA047E",
  "#926DFB",
  "#42855B",
  "#988F20",
  "#FF6D28",
  "#4B1278",
  "#D3D82C",
  "#B73E3E",
  "#256D85",
  "#47B5FF",
  "#483838",
  "#90B77D",
  "#E14D2A",
  "#06283D",
  "#D2D79F",
  "#CD1010",
  "#B8E6F8",
  "#9C2C77",
  "#3559BD",
];

export default function LinePath() {
  const { name, type } = useAppSelector((state) => state.operation);
  const { visible, pathShowType, deselectedPathes } = useAppSelector((state) => state.services);
  const { visibility } = useAppSelector((state) => state.filters);
  const { path: selectedPath } = useAppSelector((state) => state.app);
  const { pathLayer } = useAppSelector((state) => state.layers);
  const [open, setOpen] = useState(false);

  const dispatch = useAppDispatch();
  const { editPath } = useEditPath();

  const [hoveredLine, setHoveredLine] = useState<null | number>(null);
  const [showHint, setShowHint] = useState<boolean>(false);

  const editStation = (station: IStation) => {
    dispatch(changeModal({ status: "add-station", data: { ...station, isEdit: true } }));
  };

  const { data } = usePaths();

  const curvePolyline = useMemo(() => {
    if (!data) return null;

    let filteredData = visibility.length > 0 ? data.filter((path: IPath) => visibility.includes(path?.type)) : data;
    if (visible && selectedPath?.id) {
      filteredData = [selectedPath];
    }

    return filteredData.map((path, index) => {
      if (deselectedPathes.includes(path.id)) return;
      const { service } = path;
      if (!path || path.stations.length === 0) return null;
      const { stations } = path;

      const finalResult = [];

      const sourceStation = stations[0];
      const destinationStation = stations[stations.length - 1];

      const events = {
        click: () => {
          dispatch(changeShowReportRightControl(true));
          dispatch(changeShowPaths(false));
          editPath(path);
        },
      };

      const stationEvents = {
        click: () => {},
      };

      const isActive = name === "create-path" || name === "edit-path";
      const stationsLength = path.stations.length - 1;
      const markers: any = [];
      const opacity = isActive ? MAP_CONTENTS_OPACITY : 1;
      const positions: any = path.stations.map((station: IStation, indx) => {
        const tooltip = !isActive && (
          <Tooltip direction="top" offset={[0, -20]}>
            <Card sx={{ py: 2, px: 3 }}>
              <Stack alignItems="center">
                <Typography>{stations[indx].title}</Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  کد ایستگاه : {stations[indx].code}
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  عرض جغرافیایی : {stations[indx].lat}
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  طول جغرافیایی : {stations[indx].lng}
                </Typography>
              </Stack>
            </Card>
          </Tooltip>
        );

        const stationColor = STATIONS_COLOR[station?.type];
        const setBorderColor = (n: number) => {
          switch (n) {
            case 0:
              return stationColor;

            case stationsLength:
              return "#ffffff00";

            default:
              return Colors[index % Colors.length];
          }
        };
        const stationData = STATIONS2.filter((i) => i.type === station.type)[0];
        const finalSize = indx === 0 || indx === stationsLength ? STATIONS_SIZE : SMALL_STATIONS_SIZE;
        const iconMarkup = renderToStaticMarkup(
          <Box
            style={{
              width: indx === stationsLength ? "45px" : `${finalSize}px`,
              height: indx === stationsLength ? "50px" : `${finalSize}px`,
              backgroundColor: indx === stationsLength ? "#ffffff00" : "#fff",
              borderRadius: indx === stationsLength ? "none" : "50%",
              border: "2px solid",
              borderColor: setBorderColor(indx),
              display: pathShowType.includes(6) || indx === stationsLength || indx === 0 ? "grid" : "none",
              placeItems: "center",
              color: stationColor,
            }}
          >
            {indx === stationsLength ? (
              <svg
                width={65}
                height={65}
                viewBox="7 2 15 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ paddingBottom: "20px" }}
              >
                <path
                  d="M7.96891 4.24999C9.31606 1.91666 12.6839 1.91666 14.0311 4.25L18.3612 11.75C19.7084 14.0833 18.0244 17 15.3301 17H6.66986C3.97557 17 2.29163 14.0833 3.63878 11.75L7.96891 4.24999Z"
                  fill="#fff"
                  stroke={stationColor}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
            {indx === 0 || indx === stationsLength ? stationData.icon : null}
          </Box>
        );
        const icon = divIcon({
          html: iconMarkup,
          iconAnchor: [finalSize / 2, finalSize / 2],
        });
        markers.push(
          <Marker
            key={station.id}
            position={[station.lat, station.lng]}
            icon={icon}
            zIndexOffset={1}
            eventHandlers={{
              contextmenu: () => {
                // setOpen(true);
                dispatch(changeStoredStation(station));
                dispatch(changeShowContext2(true));
                setTimeout(() => dispatch(changeContextPosition2({ lat: station?.lat, lng: station?.lng })), 400);
              },
            }}
          >
            {tooltip}
          </Marker>
        );
        return [station.lat, station.lng];
      });

      return (
        <>
          {markers}
          <Polyline
            positions={positions}
            pathOptions={{ color: Colors[index % Colors.length], weight: 4 }}
            eventHandlers={events}
            opacity={opacity}
          />
        </>
      );
    });
  }, [data, visibility, name, pathShowType, deselectedPathes]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      {pathShowType.includes(5) && visible && curvePolyline}
      <Pane name="path-line" style={{ zIndex: pathLayer }} key={pathLayer}>
        {pathShowType.includes(5) && visible && curvePolyline}
      </Pane>
      <HintBox visible={showHint}>
        <Typography sx={{ m: "0 !important" }}>برای زمانبندی مسیر کلیک کنید</Typography>
      </HintBox>
    </>
  );
}
